import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { patchMetaAdsCampaignStatus } from "./useMetaAdsApi";

const graphCampaignsKey = (datePreset) => [
  "meta-ads-graph-campaigns",
  "ad-account",
  datePreset,
];

/**
 * Pause or activate a campaign (Meta `status` ACTIVE / PAUSED).
 * Optimistic cache update + refetch on settle to match Meta.
 */
export function useMetaAdsPatchCampaignStatus() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation("metaAds");
  return useMutation({
    mutationFn: async ({ campaignId, status, datePreset }) => {
      await patchMetaAdsCampaignStatus(campaignId, status);
      return { datePreset: datePreset || "last_30d" };
    },
    onMutate: async (variables) => {
      const dp = variables.datePreset || "last_30d";
      const key = graphCampaignsKey(dp);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (old) => {
        if (!old || !Array.isArray(old.campaigns)) {
          return old;
        }
        const next = variables.status;
        return {
          ...old,
          campaigns: old.campaigns.map((c) =>
            c.campaignId === variables.campaignId ? { ...c, status: next } : c,
          ),
        };
      });
      return { previous, datePreset: dp };
    },
    onError: (err, _variables, context) => {
      if (context?.previous != null) {
        queryClient.setQueryData(
          graphCampaignsKey(context.datePreset || "last_30d"),
          context.previous,
        );
      }
      showToast(
        err?.message || t("performance.campaignStatusUpdateError"),
        "error",
      );
    },
    onSettled: (_data, _error, variables, context) => {
      const dp = context?.datePreset ?? variables?.datePreset ?? "last_30d";
      queryClient.invalidateQueries({
        queryKey: graphCampaignsKey(dp),
      });
    },
  });
}
